import { convertToDate } from '@/helpers/DateHelper';
import isValid from 'date-fns/isValid';

export const blockSensitiveCharacter = (event: any) => {
  const validator = new RegExp(/[1234567890`~<>;:"[-\]|{}()=_+&#$%^*!?]/g);
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const emailCharacter = (event: any) => {
  const validator = new RegExp(/[-`~<>;:" [-\]|{}()=+&#$%^*!?]/g);
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const addressCharacter = (event: any) => {
  const validator = new RegExp(/[`~<>;:"[\]|{}()=_+#$%^*!?]/g);
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const alphabetOnly = (event: any) => {
  const validator = new RegExp(/[1234567890`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/g);
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const alphaNumericOnly = (event: any) => {
  const validator = new RegExp(/[-`@~<>;:" [-\]|{}(/.,')=_+&#$%^*!?]/g);
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const numericOnly = (event: any) => {
  const numericAllowKeys = ['Backspace', 'Delete', 'Arrow'];
  const validator = /\d/g;

  if (
    validator.test(event.key) ||
    numericAllowKeys.find((x) => event.key.includes(x)) !== undefined ||
    event.ctrlKey
  ) {
    return true;
  } else {
    event.preventDefault();
  }
};

export const setDOB = (event: any) => {
  if (!event.match(/^\d{12}$/)) {
    return null;
  }

  const nricYear = parseInt(event.substr(0, 2), 10);
  const nricMonth = parseInt(event.substr(2, 2), 10);
  const nricDay = parseInt(event.substr(4, 2), 10);

  // validate NRIC date components
  if (nricMonth < 1 || nricMonth > 12 || nricDay < 1 || nricDay > 31) {
    return null;
  }
  const currentYear = new Date().getFullYear();
  const currentYearLastTwoDigits = currentYear % 100;
  const currentYearFirstTwoDigits = Math.floor(currentYear / 100);

  // calculate year based on NRIC's first two digits
  const nricYearPrefix =
    currentYearLastTwoDigits < nricYear
      ? currentYearFirstTwoDigits - 1
      : currentYearFirstTwoDigits;
  const fullYear = parseInt(
    nricYearPrefix.toString() +
      (nricYear < 10 ? '0' + nricYear.toString() : nricYear.toString()),
    10,
  );
  // validate and create the Date object
  const dobDate = new Date(fullYear, nricMonth - 1, nricDay);

  // check if the calculated date is valid
  if (
    !(
      dobDate.getFullYear() !== fullYear ||
      dobDate.getMonth() !== nricMonth - 1 ||
      dobDate.getDate() !== nricDay
    )
  ) {
    return dobDate.toLocaleDateString('en-GB');
  } else {
    return null;
  }
};

type ValidatorType = {
  pattern: RegExp;
};

const validators: Record<string, ValidatorType> = {
  email: {
    pattern: /[-`~<>;:" [-\]|{}()=+&#$%^*!?]/g,
  },
  address: {
    pattern: /[`~<>;:"[\]|{}()=_+#$%^*!?]/g,
  },
  alphabet: {
    pattern: /[1234567890`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/g,
  },
  alphaNumeric: {
    pattern: /[-`@~<>;:" [-\]|{}(/.,')=_+&#$%^*!?]/g,
  },
  numeric: {
    pattern: /\D/g,
  },
  sensitiveCharacter: {
    pattern: /[1234567890`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/g,
  },
};

export const handlePaste = (event: any, type: string) => {
  const { pattern } = validators[type];
  const pastedText = event.clipboardData.getData('text');
  const sanitizedText = pastedText.replace(pattern, ''); // Remove disallowed characters
  event.preventDefault();

  const input = event.target as HTMLInputElement;
  input.value = sanitizedText;
};

export const capitalizeFirstChar = (letter: string) => {
  return letter.charAt(0).toUpperCase() + letter.slice(1);
};

export const capitalizeFirstLetterOfEachWord = (inputText: string) => {
  if (inputText !== undefined) {
    // Split the input text into words
    const words = inputText.split(' ');

    // Capitalize the first letter of each word
    const capitalizedWords = words.map((word) => {
      if (word.length > 0) {
        return word[0].toUpperCase() + word.slice(1);
      }
      return word; // Handle empty words if necessary
    });

    // Join the capitalized words back into a string
    const capitalizedText = capitalizedWords.join(' ');

    return capitalizedText;
  }
};

export const DOBInputChange = (e: any, date: any, sep = '/') => {
  let re = new RegExp(sep, 'g');
  const hasInvalidChars = /[^0-9]/.test((e.target.value + '').replace(re, ''));
  let error = hasInvalidChars
    ? 'errors.invalidDate.onlyNumberAndSeparator'
    : undefined;
  let valid, sepCount;
  const value = e.target.value + '';
  let newValue = value;
  const oldLen = (date + '').length;
  const newLen = value.length;
  let shouldValidate = false;

  if (!error) {
    re = new RegExp(sep + sep);
    const doubleSep = !!value.match(re);
    if (newLen > oldLen) {
      // user enter new input, so check it
      if (value.length === 1 && value === sep) {
        error = 'errors.invalidDate.firstCharacter';
      } else if (doubleSep) {
        error = 'errors.invalidDate.no2ConsecutiveSlash';
      } else {
        re = new RegExp(sep, 'g');
        sepCount = value.match(re) ? value.match(re)?.length : 0;
        if (sepCount === 0) {
          error = /^(0?[1-9]?|[12][0-9]|3[01])$/.test(value)
            ? undefined
            : 'errors.invalidDate.between1to31';
        } else if (sepCount === 1) {
          if (!value.endsWith(sep)) {
            re = new RegExp(
              `^(0?[1-9]|[12][0-9]|3[01])[${sep}](0?[1-9]?|1[012])$`,
            );
            valid = value.match(re);
            if (valid) {
              const dd = parseInt(valid[1]);
              const mm = parseInt(valid[2]);
              if (mm > 1) {
                // can only check if 2 - 9
                if (mm === 2 && dd > 29) {
                  error = 'errors.invalidDate.feb';
                } else {
                  const isShortMonth = [4, 6, 9, 11].indexOf(mm) >= 0;
                  if (isShortMonth && dd > 30)
                    error = 'errors.invalidDate.dayMonthCombine';
                }
              }
            } else {
              error = 'errors.invalidDate.format';
            }
          }
        } else if (sepCount === 2) {
          const index = value.lastIndexOf(sep);
          const mmdd = value.substring(0, index);
          const yyyy = value.substring(index + 1); // if value = '12/1/' then yyyy = '' even though index+1 greater than length of string
          re = new RegExp(
            `^(0?[1-9]|[12][0-9]|3[01])[${sep}](0?[1-9]|1[012])$`,
          );
          error = re.test(mmdd) ? undefined : 'Invalid day and month format';
          if (!error) {
            if (yyyy.length === 1) {
              error = /^([1-2])$/.test(yyyy)
                ? undefined
                : 'errors.invalidDate.yearNot1Or2';
            } else if (yyyy.length === 2 || yyyy.length === 3) {
              error = /^(19|20)\d?$/.test(yyyy)
                ? undefined
                : 'errors.invalidDate.yearNot19Or20';
            } else if (yyyy.length === 4) {
              error = /^(19|20)\d\d$/.test(yyyy)
                ? undefined
                : 'errors.invalidDate.incorrectYear';
              if (!error) {
                // month is 0-based, that's why we need dataParts[1] - 1
                const newDate = convertToDate(value);
                valid = isValid(newDate);
                if (valid) {
                  shouldValidate = true;
                } else {
                  error = 'errors.invalid.date';
                }
              }
            } else if (yyyy.length > 4) {
              error = 'errors.invalidDate.dyearTooBig';
            }
          }
        }
      }
    }
  }

  // if there are no errors and user has keyed in new data, we can try and do auto-expansion
  if (newLen > oldLen && !error) {
    re = new RegExp(sep, 'g');
    sepCount = value.match(re) ? value.match(re)?.length : 0;
    if (sepCount === 0) {
      if ((value.length === 1 && /^[4-9]$/.test(value)) || value.length === 2) {
        newValue =
          (value > 0 && value < 10 && !value.includes(0) ? 0 + value : value) +
          sep;
      }
    } else if (sepCount === 1 && value.length === 2) {
      newValue =
        (value[0] > 0 && value[0] < 10 ? 0 + value[0] : value[0]) + sep;
    } else if (sepCount === 1) {
      const idx = value.lastIndexOf(sep);
      let endValue = value.substring(idx + 1); // get month
      if (
        (endValue.length === 1 && /^[2-9]$/.test(endValue)) ||
        endValue.length === 2
      ) {
        if (endValue.length === 1) {
          // If user key in between 2-9
          if (endValue > 0 && endValue < 10) endValue = '0' + endValue;
          newValue = value.slice(0, 3) + endValue + sep;
        } else {
          newValue = value + sep;
        }
      }
    } else if (sepCount === 2 && value.includes('/', 4) && value.length === 5) {
      // If user key 1st month and key in "/"
      let endValue = value[3];
      if (endValue > 0 && endValue < 10) endValue = '0' + endValue;
      newValue = value.slice(0, 3) + endValue + sep;
    }
    return newValue;
  } else {
    // there are errors or user deleted characters
    return error ? error : 'errors.invalidDate.emptyDate';
  }
};

export const sanitisedMobileNumber = (value: string) => {
  const sanitizedValue = value.replace(/\D/g, '');

  if (sanitizedValue.charAt(0) === '0') {
    return sanitizedValue.slice(1);
  } else {
    return sanitizedValue;
  }
};
